/**
 * Entity Workflows Panel
 * 
 * Panel component that displays workflows attached to an entity (Part, Opportunity, etc.)
 * with the ability to start new workflows and view existing ones.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import {
  Workflow,
  ChevronDown,
  ChevronRight,
  Plus,
  Clock,
  CheckCircle2,
  Play,
  User,
  Calendar,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import * as workflowApi from '../../api/workflows';
import StartWorkflowDialog from './StartWorkflowDialog';
import WorkflowDetailDrawer from './WorkflowDetailDrawer';

// Utility for class names
const cn = (...classes) => classes.filter(Boolean).join(' ');

const STATUS_CONFIG = {
  active: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: Play },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', icon: Play },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-700', icon: AlertCircle },
};

function WorkflowInstanceCard({ instance, onClick }) {
  const statusConfig = STATUS_CONFIG[instance.status] || STATUS_CONFIG.active;
  const StatusIcon = statusConfig.icon;
  const currentStep = instance.current_step_name || 'Not started';
  
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const completedSteps = instance.step_instances?.filter(s => s.status === 'completed').length || 0;
  const totalSteps = instance.step_instances?.length || 0;
  const progress = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  return (
    <div
      onClick={onClick}
      className="p-3 bg-gray-50 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
      data-testid={`workflow-instance-${instance.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 text-sm truncate">
            {instance.name || instance.template?.name || 'Workflow'}
          </h4>
          
          <div className="flex items-center gap-2 mt-1">
            <Badge className={cn("text-xs", statusConfig.color)}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </Badge>
            
            {instance.status === 'active' && (
              <span className="text-xs text-gray-500">
                Step: {currentStep}
              </span>
            )}
          </div>

          {/* Progress bar */}
          {totalSteps > 0 && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>{completedSteps} of {totalSteps} steps</span>
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={cn(
                    "h-full rounded-full transition-all",
                    instance.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'
                  )}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            {instance.created_at && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(instance.created_at)}
              </span>
            )}
          </div>
        </div>
        
        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
      </div>
    </div>
  );
}

export default function EntityWorkflowsPanel({
  entityType,
  entityId,
  entityName,
  defaultOpen = false,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);

  // Fetch workflows for this entity
  const { data: workflowsData, isLoading, error, refetch } = useQuery({
    queryKey: ['entity-workflows', entityType, entityId],
    queryFn: () => workflowApi.getEntityWorkflows(entityType, entityId),
    enabled: !!entityId,
  });

  const workflows = workflowsData?.data || [];
  const activeWorkflows = workflows.filter(w => w.status === 'active' || w.status === 'in_progress');
  const completedWorkflows = workflows.filter(w => w.status === 'completed');

  const handleWorkflowStarted = () => {
    refetch();
  };

  return (
    <>
      <Card data-testid="entity-workflows-panel">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Workflow className="w-4 h-4 text-blue-600" />
                  Workflows
                  {activeWorkflows.length > 0 && (
                    <Badge className="bg-blue-100 text-blue-700 ml-2">
                      {activeWorkflows.length} active
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowStartDialog(true);
                    }}
                    data-testid="start-workflow-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Start Workflow
                  </Button>
                  {isOpen ? (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  )}
                </div>
              </div>
            </CardHeader>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <CardContent className="pt-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : error ? (
                <div className="text-center py-6 text-red-500 text-sm">
                  Failed to load workflows
                </div>
              ) : workflows.length === 0 ? (
                <div className="text-center py-6">
                  <Workflow className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No workflows started</p>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setShowStartDialog(true)}
                    className="mt-1"
                  >
                    Start a workflow
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Active Workflows */}
                  {activeWorkflows.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <Play className="w-3 h-3" />
                        Active Workflows
                      </h4>
                      <div className="space-y-2">
                        {activeWorkflows.map((instance) => (
                          <WorkflowInstanceCard
                            key={instance.id}
                            instance={instance}
                            onClick={() => setSelectedInstance(instance)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Completed Workflows */}
                  {completedWorkflows.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <CheckCircle2 className="w-3 h-3" />
                        Completed Workflows
                      </h4>
                      <div className="space-y-2">
                        {completedWorkflows.slice(0, 3).map((instance) => (
                          <WorkflowInstanceCard
                            key={instance.id}
                            instance={instance}
                            onClick={() => setSelectedInstance(instance)}
                          />
                        ))}
                        {completedWorkflows.length > 3 && (
                          <p className="text-xs text-gray-500 text-center">
                            +{completedWorkflows.length - 3} more completed
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Start Workflow Dialog */}
      <StartWorkflowDialog
        open={showStartDialog}
        onOpenChange={setShowStartDialog}
        entityType={entityType}
        entityId={entityId}
        entityName={entityName}
        onSuccess={handleWorkflowStarted}
      />

      {/* Workflow Detail Drawer */}
      {selectedInstance && (
        <WorkflowDetailDrawer
          open={!!selectedInstance}
          onOpenChange={(open) => !open && setSelectedInstance(null)}
          instanceId={selectedInstance.id}
          onUpdate={refetch}
        />
      )}
    </>
  );
}
